import {
  Box,
  Button,
  Divider,
  FormHelperText,
  Stack,
  Typography,
} from "@mui/material";
import appleIconSvg from "../../../../assets/apple_icon_desktop.svg";
import googlePlayIconSvg from "../../../../assets/google_play_icon_desktop.svg";
import { FONT_COLORS } from "../../../../constants";
import { PlanLimitAlert, TabSwitch } from "../../../../components";
import { PricingPlanItem } from "../PricingPlanItem";
import { ExternalStoreNotice } from "../ExternalStoreNotice";
import { useState } from "react";
import {
  TBillingPeriod,
  TPricingPlanItem,
} from "../../../../features/pricingPlans/pricingPlans.types";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../../store/hooks";
import { selectExtension } from "../../../../features/extension/extension.selector";
import { selectUserProfile } from "../../../../features/user/user.selector";
import useCheckout from "../../../../hooks/useCheckout";
import iosUiSvg from "../../../../assets/ios_ui.svg";

const APP_STORE_STEPS = [
  "Open the Settings app on your iPhone or iPad",
  "Tap your name at the top",
  "Tap Subscriptions",
  "Select SellerScout",
  "Choose your plan or make changes",
];

const PLAY_STORE_STEPS = [
  "Open the Google Play Store app on your Android device",
  "Tap your profile icon in the top right",
  "Tap Payments & subscriptions, then Subscriptions",
  "Select SellerScout",
  "Choose your plan or make changes",
];

type TPeriodOptions = {
  value: TBillingPeriod;
  label: string;
};

type TPricingPlansProps = {
  plans: TPricingPlanItem[];
  currentPlanId?: string;
  redirectUrl: string;
};

const SWITCH_OPTIONS: TPeriodOptions[] = [
  { value: "monthly", label: "Monthly" },
  { value: "annually", label: "Annually" },
];

export const PricingPlans = ({
  plans,
  currentPlanId,
  redirectUrl,
}: TPricingPlansProps) => {
  const navigate = useNavigate();
  const { checkout } = useCheckout();
  const { isExtension } = useAppSelector(selectExtension);
  const user = useAppSelector(selectUserProfile);
  const [billingPeriod, setBillingPeriod] = useState<TBillingPeriod>("monthly");
  const [updatingPlan, setUpdatingPlan] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const plansByPeriod = plans
    .filter((plan) => plan.period === billingPeriod || plan.period === null)
    .sort((a, b) => (a.price > b.price ? 1 : -1));

  const createPaymentIntent = async (planId: string) => {
    setUpdatingPlan(true);
    setError(null);

    try {
      const plan = plansByPeriod.find((item) => item.id === planId);

      if (!plan) {
        throw new Error("The plan does not exist");
      }

      await checkout({ plan, redirectUrl });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setUpdatingPlan(false);
    }
  };

  if (user?.store === "PLAY_STORE") {
    return (
      <ExternalStoreNotice
        icon={googlePlayIconSvg}
        iconAlt="Google Play"
        title="Your subscription is managed in Google Play."
        subtitle="You subscribed to this plan on Google Play. Please use Google Play to change your plan."
        stepsTitle="Manage your subscription on Android"
        stepsTitleMobile="Manage on Android"
        steps={PLAY_STORE_STEPS}
      />
    );
  }

  if (user?.store === "APP_STORE") {
    return (
      <ExternalStoreNotice
        icon={appleIconSvg}
        iconAlt="Apple"
        title="Your subscription is managed in the App Store."
        subtitle="Because you subscribed on an iOS device using Apple, you'll need to manage your subscription in the App Store."
        stepsTitle="Manage your subscription on iOS"
        stepsTitleMobile="Manage on iOS"
        steps={APP_STORE_STEPS}
        illustration={iosUiSvg}
        illustrationAlt="iOS Settings"
      />
    );
  }

  return (
    <Box>
      <Box mb={2}>
        <PlanLimitAlert />
      </Box>

      {/* xs pill toggle */}
      <Box display={{ xs: "flex", sm: "none" }} justifyContent="center" mb={3}>
        <Box
          sx={{
            display: "flex",
            backgroundColor: "#F3F4F6",
            borderRadius: "8px",
            p: "4px",
          }}
        >
          {SWITCH_OPTIONS.map(({ value, label }) => (
            <Box
              key={value}
              onClick={() => setBillingPeriod(value)}
              sx={{
                px: 3,
                py: 1,
                borderRadius: "6px",
                cursor: "pointer",
                backgroundColor:
                  billingPeriod === value ? "#fff" : "transparent",
                boxShadow:
                  billingPeriod === value
                    ? "0px 1px 4px rgba(0,0,0,0.1)"
                    : "none",
                transition: "all 0.2s",
              }}
            >
              <Typography
                fontSize="14px"
                fontWeight={billingPeriod === value ? 600 : 400}
                color={billingPeriod === value ? "#171A1F" : "#6b7280"}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* sm+ header + TabSwitch */}
      <Stack
        sx={{ display: { xs: "none", sm: "flex" } }}
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "center", sm: "flex-end" }}
        spacing={{ xs: 2, sm: 0 }}
        mb={1.5}
      >
        <Box>
          <Typography
            fontSize={18}
            fontWeight={600}
            color={FONT_COLORS.default}
          >
            Pricing Plans
          </Typography>
          {!!currentPlanId && (
            <Button sx={{ px: 0 }} onClick={() => navigate("/account/billing")}>
              Current Plan Details
            </Button>
          )}
          {error && <FormHelperText error>{error}</FormHelperText>}
        </Box>
        <TabSwitch
          options={SWITCH_OPTIONS}
          value={billingPeriod}
          onChange={setBillingPeriod}
        />
      </Stack>

      {/* xs error */}
      {error && (
        <FormHelperText
          error
          sx={{
            display: { xs: "block", sm: "none" },
            mb: 1,
            textAlign: "center",
          }}
        >
          {error}
        </FormHelperText>
      )}

      <Box sx={{ display: { xs: "none", sm: "block" } }}>
        <Divider />
      </Box>

      <Stack
        direction={{ xs: "column", md: "row" }}
        flexWrap="wrap"
        justifyContent="center"
        alignContent="center"
        alignItems="center"
        gap={{ xs: 2, sm: isExtension ? 1 : 3 }}
        mt={{ xs: 0, sm: isExtension ? 1 : 3 }}
        px={{ xs: 2, sm: 0 }}
      >
        {plansByPeriod.map((plan) => (
          <PricingPlanItem
            key={plan.id}
            plan={plan}
            currentPlanId={currentPlanId}
            onPlanChange={createPaymentIntent}
            isLoading={updatingPlan}
          />
        ))}
      </Stack>
    </Box>
  );
};
